import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CourseProvider, useCourse } from './contexts/CourseContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { GamificationProvider } from './contexts/GamificationContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { Lock } from 'lucide-react';
import { AppShell, Button, EmptyState, Loader } from './components/ui';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import CourseSelector from './pages/CourseSelector';
import Home from './pages/Home';
import ExamConfig from './pages/ExamConfig';
import ExamPage from './pages/ExamPage';
import ExamHistory from './pages/ExamHistory';
import Results from './pages/Results';
import StudyHome from './pages/StudyHome';
import TopicView from './pages/TopicView';
import LessonView from './pages/LessonView';
import ErrorNotebook from './pages/ErrorNotebook';
import SearchResults from './pages/SearchResults';
import FlashcardReview from './pages/FlashcardReview';
import Account from './pages/Account';
import AdminUsers from './pages/AdminUsers';
import AdminArticleReviews from './pages/AdminArticleReviews';
import AdminAI from './pages/AdminAI';
import Achievements from './pages/Achievements';
import Articles from './pages/Articles';
import Notifications from './pages/Notifications';
import ArticleTag from './pages/ArticleTag';
import ArticleEditor from './pages/ArticleEditor';
import ArticleView from './pages/ArticleView';
import PublicArticle from './pages/PublicArticle';
import PublicMedal from './pages/PublicMedal';
import RequestAccess from './pages/RequestAccess';
import Courses from './pages/Courses';
import Landing from './pages/Landing';
import Apresentacao from './pages/Apresentacao';
import PublicArticles from './pages/PublicArticles';
import Trails from './pages/Trails';
import TrailView from './pages/TrailView';
import TrailPlan from './pages/TrailPlan';
import TrailExam from './pages/TrailExam';
import Onboarding from './pages/Onboarding';
import { Tour } from './components/Tour';
import { DiveProvider } from './components/DiveTransition';
import './App.css';

// Etapa e editor carregam o pdf.js: ficam fora do pacote principal
const TrailStage = lazy(() => import('./pages/TrailStage'));
const TrailEditor = lazy(() => import('./pages/TrailEditor'));

/** Página nova começa do topo (o navegador mantinha a rolagem da anterior). */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/**
 * Exige login; com troca de senha pendente, só deixa abrir a tela de troca.
 * Aluno que ainda não passou pelo primeiro acesso vai para /boas-vindas.
 */
function RequireAuth() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loader label="Carregando..." />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (user.mustChangePassword) return <Navigate to="/trocar-senha" replace />;
  if (!user.onboarded && location.pathname !== '/boas-vindas') return <Navigate to="/boas-vindas" replace />;
  return <Outlet />;
}

/** Rotas de curso: só abrem se o curso estiver entre os liberados para o usuário. */
function CourseGuard() {
  const { courseSlug } = useParams();
  const { courses, loading } = useCourse();
  if (loading) return <Loader />;
  if (!courses.some(c => c.slug === courseSlug)) {
    return (
      <AppShell section="Sem acesso">
        <EmptyState
          file="acesso.xml"
          title="Você não tem acesso a este curso"
          description="Peça ao administrador para liberar o curso na sua conta."
          action={<Button icon={Lock} onClick={() => window.location.assign('/')}>Ver meus cursos</Button>}
        />
      </AppShell>
    );
  }
  return <Outlet />;
}

/** Raiz: quem não está logado vê a landing; logado, a tela inicial (com as mesmas regras do RequireAuth). */
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Loader label="Carregando..." />;
  if (!user) return <Landing />;
  if (user.mustChangePassword) return <Navigate to="/trocar-senha" replace />;
  if (!user.onboarded) return <Navigate to="/boas-vindas" replace />;
  return <CourseSelector />;
}

function RequireAdmin() {
  const { isAdmin } = useAuth();
  return isAdmin ? <Outlet /> : <Navigate to="/" replace />;
}

function RequireSuperAdmin() {
  const { isSuperAdmin } = useAuth();
  return isSuperAdmin ? <Outlet /> : <Navigate to="/" replace />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CourseProvider>
          <GamificationProvider>
          <NotificationsProvider>
          <Router>
            <DiveProvider>
            <ScrollToTop />
            <Tour />
            <div className="App">
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/trocar-senha" element={<ChangePassword />} />
                <Route path="/solicitar-acesso" element={<RequestAccess />} />
                {/* Artigo público: abre sem login; logado, vai para a versão completa */}
                <Route path="/a/:slug" element={<PublicArticle />} />
                <Route path="/m/:token" element={<PublicMedal />} />
                <Route path="/a" element={<PublicArticles />} />
                {/* Landing pública e apresentação */}
                <Route path="/" element={<RootRoute />} />
                <Route path="/conheca" element={<Landing />} />
                <Route path="/apresentacao" element={<Apresentacao />} />

                <Route element={<RequireAuth />}>
                  <Route path="/boas-vindas" element={<Onboarding />} />
                  <Route path="/preferencias" element={<Onboarding />} />
                  <Route path="/conta" element={<Account />} />
                  <Route path="/cursos" element={<Courses />} />
                  <Route path="/trilhas" element={<Trails />} />
                  <Route path="/trilhas/:slug" element={<TrailView />} />
                  <Route path="/trilhas/:slug/etapas/:n" element={<Suspense fallback={<Loader />}><TrailStage /></Suspense>} />
                  <Route path="/trilhas/:slug/plano" element={<TrailPlan />} />
                  <Route path="/trilhas/:slug/prova" element={<TrailExam />} />
                  <Route path="/notificacoes" element={<Notifications />} />
                  <Route path="/artigos" element={<Articles />} />
                  <Route path="/artigos/novo" element={<ArticleEditor />} />
                  <Route path="/artigos/tema/:slug" element={<ArticleTag />} />
                  <Route path="/artigos/:id/editar" element={<ArticleEditor />} />
                  <Route path="/artigos/:slug" element={<ArticleView />} />

                  <Route element={<RequireSuperAdmin />}>
                    <Route path="/admin/usuarios" element={<AdminUsers />} />
                    <Route path="/admin/ia" element={<AdminAI />} />
                  </Route>
                  <Route element={<RequireAdmin />}>
                    <Route path="/admin/revisao" element={<AdminArticleReviews />} />
                    <Route path="/trilhas/:slug/editar" element={<Suspense fallback={<Loader />}><TrailEditor /></Suspense>} />
                  </Route>

                  <Route path="/home" element={<Home />} />
                  <Route element={<CourseGuard />}>
                  <Route path="/curso/:courseSlug" element={<Home />} />
                  <Route path="/curso/:courseSlug/estudo" element={<StudyHome />} />
                  <Route path="/curso/:courseSlug/estudo/:topicId" element={<TopicView />} />
                  <Route path="/curso/:courseSlug/estudo/:topicId/aula/:lessonId" element={<LessonView />} />
                  <Route path="/curso/:courseSlug/simulados" element={<ExamHistory />} />
                  <Route path="/curso/:courseSlug/config" element={<ExamConfig />} />
                  <Route path="/curso/:courseSlug/exam/:examId" element={<ExamPage />} />
                  <Route path="/curso/:courseSlug/results/:examId" element={<Results />} />
                  <Route path="/curso/:courseSlug/flashcards" element={<FlashcardReview />} />
                  <Route path="/curso/:courseSlug/erros" element={<ErrorNotebook />} />
                  <Route path="/curso/:courseSlug/conquistas" element={<Achievements />} />
                  <Route path="/curso/:courseSlug/buscar" element={<SearchResults />} />
                  </Route>

                  {/* Rotas antigas, de antes dos cursos */}
                  <Route path="/estudo" element={<Navigate to="/curso/software-architecture/estudo" replace />} />
                  <Route path="/estudo/:topicId" element={<LegacyRedirect to={(p) => `/curso/software-architecture/estudo/${p.topicId}`} />} />
                  <Route path="/estudo/:topicId/aula/:lessonId" element={<LegacyRedirect to={(p) => `/curso/software-architecture/estudo/${p.topicId}/aula/${p.lessonId}`} />} />
                  <Route path="/config" element={<Navigate to="/curso/software-architecture/config" replace />} />
                  <Route path="/exam/:examId" element={<LegacyRedirect to={(p) => `/curso/software-architecture/exam/${p.examId}`} />} />
                  <Route path="/results/:examId" element={<LegacyRedirect to={(p) => `/curso/software-architecture/results/${p.examId}`} />} />
                  <Route path="/erros" element={<Navigate to="/curso/software-architecture/erros" replace />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
            </DiveProvider>
          </Router>
          </NotificationsProvider>
          </GamificationProvider>
        </CourseProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

function LegacyRedirect({ to }) {
  const params = useParams();
  return <Navigate to={to(params)} replace />;
}

export default App;
